/**
 * Разбор состояния встречи, приходящего с сервера.
 *
 * Вынесено из `livekit.ts` намеренно: там на верхнем уровне подключается
 * SDK LiveKit, который требует настоящий браузер и в тестах не грузится.
 * А проверять здесь есть что — от этих трёх функций зависит, кто получит
 * права организатора и увидит ли докладчик предупреждение о показе окна.
 */

/**
 * Организатор ли участник.
 *
 * Признак приходит в метаданных, которые сервер положил в токен и
 * подписал своим секретом. Участник переписать их не может: грант
 * `canUpdateOwnMetadata` намеренно не выдаётся (см. `/api/token`).
 * Поэтому признаку можно верить и на чужих участниках.
 *
 * Всё, что не является явным `true`, считается «не организатор».
 * Метаданные приходят по сети, и любая неожиданность здесь — испорченный
 * JSON, чужая версия клиента, подстановка — должна вести к меньшим
 * правам, а не к большим.
 */
export function readHostFlag(metadata: string | undefined | null): boolean {
  if (!metadata) return false;
  try {
    return JSON.parse(metadata)?.host === true;
  } catch {
    return false;
  }
}

/** Состояние комнаты, общее для всех: приходит в метаданных комнаты. */
export interface RoomSettings {
  presentation: boolean;
}

export function readRoomSettings(metadata: string | undefined | null): RoomSettings {
  if (!metadata) return { presentation: false };
  try {
    return { presentation: JSON.parse(metadata)?.presentation === true };
  } catch {
    return { presentation: false };
  }
}

/**
 * Подсказка, если человек выбрал для показа окно, а не экран целиком.
 *
 * ── Что именно ломается ─────────────────────────────────────────────
 *
 * Захват отдельного окна перестаёт работать ровно тогда, когда нужен
 * больше всего — когда приложение разворачивают на весь экран. В macOS
 * такое окно уезжает в отдельное пространство (Space), а захватчик
 * продолжает снимать прежний идентификатор: собеседники видят застывший
 * кадр или чёрный прямоугольник. В Windows то же самое с полноэкранным
 * показом PowerPoint и просмотрщиков PDF — показ открывает новое окно
 * поверх, а снимается по-прежнему старое.
 *
 * Захват экрана целиком от этого не страдает. Поэтому диалог открывается
 * сразу на вкладке «Весь экран», а если человек всё же выбрал окно —
 * он узнаёт об этом сразу, а не через двадцать минут доклада в пустоту.
 */
export function screenShareHint(
  track: { getSettings(): { displaySurface?: string } } | undefined | null,
): string {
  if (!track) return '';

  let surface: string | undefined;
  try {
    surface = track.getSettings().displaySurface;
  } catch {
    // Браузер не отдаёт настройки дорожки — молчим: ложная тревога хуже,
    // чем её отсутствие.
    return '';
  }

  if (surface !== 'window') return '';
  return (
    'Вы показываете отдельное окно. Если развернуть его на весь экран, ' +
    'система перестанет его видеть — надёжнее выбрать «Весь экран».'
  );
}
